import React from 'react';
import PropTypes from 'prop-types';

import {Box, Divider, Stack} from 'views/components';
import {Tab, TabContext, TabList, TabPanel} from 'views/components';
import {ClientJobs, FamilyMembers, translate} from 'views/module';

import ClientHeader from './header/ClientHeader';
import Workflows from './workflows/Workflows';
import IntakeForm from './intake_form/IntakeForm';
import LoomCorrespondance from './correspondance/LoomCorrespondance';
import Careplan from './careplan/Careplan';
import CaregiverConnection from './caregiver_connection/CaregiverConnection';
import CareviewSettings from './careview_settings/CareviewSettings';

import FLAGS, {useFlag} from 'utils/feature_flags/flags';

export const LOOM_LEAD_FEED_TABS = [
  'WORKFLOWS',
  'INTAKE_FORM',
  'CORRESPONDANCE',
  'CAREPLAN',
  'CAREGIVER',
  'RELATIONSHIP',
  'CAREVIEW_SETTINGS',
  'JOBS',
];

const TAB_TITLES = {
  WORKFLOWS: 'loomLead.LoomLeadDetail.Workflows.Title',
  INTAKE_FORM: 'loomLead.LoomLeadDetail.IntakeForm.Title',
  CORRESPONDANCE: 'loomLead.LoomLeadDetail.Correspondance.Title',
  CAREPLAN: 'loomLead.LoomLeadDetail.Careplan.Title',
  CAREGIVER: 'loomLead.LoomLeadDetail.CaregiverConnections.Title',
  RELATIONSHIP: 'family.FamilyMembers.Title',
  CAREVIEW_SETTINGS: 'loomLead.LoomLeadDetail.CareviewSettings.Title',
  JOBS: 'job.ClientJobs.Title',
};

// The client feed of a Loom lead. It follows the client view's header and tab layout, has only
// the tabs Loom can feed, and every tab but Family is read-only.
const LoomLeadFeed = (props) => {
  const {
    clientId,
    clientData,
    clientCareplan,
    clientCareviewSettings,
    feedTab,
    setFeedTab,
    refresh,
    setRefresh,
  } = props;

  const clientJobReadFlag = useFlag(FLAGS.CLIENT_JOB_READS_FROM_LOOM);
  const feedTabs = clientJobReadFlag.isEnabled ?
    LOOM_LEAD_FEED_TABS :
    LOOM_LEAD_FEED_TABS.filter((tab) => tab !== 'JOBS');

  const reloadClient = React.useCallback(() => setRefresh((value) => value + 1), [setRefresh]);

  const handleChange = (event, newValue) => {
    setFeedTab(newValue);
  };

  return (
    <Stack spacing={2} divider={<Divider />}>
      <ClientHeader
        clientData={clientData}
        clientCareplan={clientCareplan}
        setRefresh={setRefresh}
      />
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <TabContext
          value={feedTab}
        >
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {
              feedTabs.map((tab) => (
                <Tab
                  key={tab}
                  label={translate(TAB_TITLES[tab])}
                  value={tab}
                />
              ))
            }
          </TabList>
          <TabPanel
            value={'WORKFLOWS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Workflows
              clientId={clientId}
              refresh={refresh}
            />
          </TabPanel>
          <TabPanel
            value={'INTAKE_FORM'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <IntakeForm
              clientId={clientId}
              refresh={refresh}
            />
          </TabPanel>
          <TabPanel
            value={'CORRESPONDANCE'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <LoomCorrespondance
              clientId={clientId}
              refresh={refresh}
            />
          </TabPanel>
          <TabPanel
            value={'CAREPLAN'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Careplan
              clientCareplan={clientCareplan}
            />
          </TabPanel>
          <TabPanel
            value={'CAREGIVER'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <CaregiverConnection
              clientData={clientData}
            />
          </TabPanel>
          <TabPanel
            value={'RELATIONSHIP'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <FamilyMembers
              clientId={clientId}
              familyName={clientData.familyName}
              familyMembers={clientData.familyMembers}
              familyNotes={clientData.familyNotes}
              clientNotes={clientData.clientNotes}
              reload={reloadClient}
            />
          </TabPanel>
          <TabPanel
            value={'CAREVIEW_SETTINGS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <CareviewSettings
              clientCareviewSettings={clientCareviewSettings}
            />
          </TabPanel>
          {
            clientJobReadFlag.isEnabled &&
            <TabPanel
              value={'JOBS'}
              sx={{
                m: 0, p: 0,
              }}
            >
              <ClientJobs
                clientId={clientId}
              />
            </TabPanel>
          }
        </TabContext>
      </Box>
    </Stack>
  );
};
LoomLeadFeed.propTypes = {
  clientId: PropTypes.string.isRequired,
  clientData: PropTypes.object.isRequired,
  clientCareplan: PropTypes.object.isRequired,
  clientCareviewSettings: PropTypes.object.isRequired,
  feedTab: PropTypes.string.isRequired,
  setFeedTab: PropTypes.func.isRequired,
  refresh: PropTypes.number.isRequired,
  setRefresh: PropTypes.func.isRequired,
};

export default React.memo(LoomLeadFeed);
